/**
 * 다이어그램 공통 SVG 프리미티브.
 * - SvgNode: 라벨이 있는 둥근 노드(글로우/플로팅 애니메이션은 SCSS)
 * - SvgFlow: 노드 사이를 잇는 "흐르는" 연결선(점선 stroke-dashoffset 애니메이션)
 * - FlowDefs: 화살표 마커 정의(다이어그램별 idPrefix 로 충돌 방지)
 *
 * 좌표는 각 다이어그램의 viewBox 기준 사용자 단위.
 */

export type FlowColor = 'cyan' | 'purple' | 'accent';

export const FLOW_HEX: Record<FlowColor, string> = {
  cyan: '#2ad6e6',
  purple: '#9a7bff',
  accent: '#e0455a',
};

export type NodeVariant = 'core' | 'cyan' | 'purple' | 'muted';

interface SvgNodeProps {
  x: number;
  y: number;
  w?: number;
  h?: number;
  title: string;
  sub?: string;
  variant?: NodeVariant;
  /** float 애니메이션 시작 지연(초) — 노드마다 살짝 다르게 */
  delay?: number;
  titleSize?: number;
  rx?: number;
}

export function SvgNode({
  x,
  y,
  w = 150,
  h = 58,
  title,
  sub,
  variant,
  delay = 0,
  titleSize = 14,
  rx = 12,
}: SvgNodeProps) {
  const cx = x + w / 2;
  const cy = y + h / 2;
  return (
    <g className={`ai-svg-node ${variant ? `is-${variant}` : ''}`} style={{ animationDelay: `${delay}s` }}>
      <rect x={x} y={y} width={w} height={h} rx={rx} />
      <text
        className="ai-node-title"
        x={cx}
        y={sub ? cy - 7 : cy}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={titleSize}
      >
        {title}
      </text>
      {sub && (
        <text className="ai-node-sub" x={cx} y={cy + 11} textAnchor="middle" dominantBaseline="middle" fontSize={11}>
          {sub}
        </text>
      )}
    </g>
  );
}

export function FlowDefs({ idPrefix }: { idPrefix: string }) {
  return (
    <defs>
      {(Object.keys(FLOW_HEX) as FlowColor[]).map((k) => (
        <marker
          key={k}
          id={`${idPrefix}-arw-${k}`}
          markerWidth="9"
          markerHeight="9"
          refX="6"
          refY="3"
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill={FLOW_HEX[k]} />
        </marker>
      ))}
    </defs>
  );
}

interface SvgFlowProps {
  d: string;
  idPrefix: string;
  color?: FlowColor;
  reverse?: boolean;
  slow?: boolean;
  delay?: number;
  arrow?: boolean;
}

export function SvgFlow({ d, idPrefix, color = 'cyan', reverse, slow, delay = 0, arrow = true }: SvgFlowProps) {
  return (
    <g>
      <path className="ai-flow-base" d={d} />
      <path
        className={`ai-flow-line ${reverse ? 'is-rev' : ''} ${slow ? 'is-slow' : ''}`}
        d={d}
        stroke={FLOW_HEX[color]}
        markerEnd={arrow ? `url(#${idPrefix}-arw-${color})` : undefined}
        style={{ animationDelay: `${delay}s` }}
      />
    </g>
  );
}
